import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useSubscription } from '../hooks/useSubscription';

const CheckoutSuccess: React.FC = () => {
  const { getToken } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyCheckout = async () => {
      // Get parameters from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const feature = urlParams.get('feature');
      const returnContext = urlParams.get('return_context');
      
      console.log('[CheckoutSuccess] URL params:', { sessionId, feature, returnContext });

      try {
        // Get auth token
        const token = await getToken();
        if (!token) {
          setError('Authentication required');
          setIsVerifying(false);
          return;
        }

        // Verify the subscription with backend
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/api/billing/verify-checkout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          // Show more helpful message for webhook processing delay
          if (response.status === 404 && errorData.message?.includes('still being processed')) {
            setError(errorData.message || 'Subscription is still being processed. Please wait a moment and refresh the page.');
          } else {
            setError(errorData.message || errorData.error || 'Failed to verify subscription');
          }
          setIsVerifying(false);
          return;
        }

        const data = await response.json();
        console.log('[CheckoutSuccess] Subscription verified:', data);
        
        // Refresh subscription state to get the new plan
        console.log('[CheckoutSuccess] Refreshing subscription state...');
        await refreshSubscription();
        console.log('[CheckoutSuccess] Subscription state refreshed');
        
        setIsVerifying(false);
        
        // Determine where to redirect based on context
        let redirectUrl = '/';
        
        if (returnContext) {
          // Use the return context if provided
          redirectUrl = returnContext;
        } else if (feature) {
          // Store the feature in sessionStorage so the app can handle it
          sessionStorage.setItem('pendingFeature', feature);
        }
        
        console.log('[CheckoutSuccess] Redirecting to:', redirectUrl);
        
        // Redirect after 2 seconds
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 2000);
        
      } catch (err) {
        console.error('[CheckoutSuccess] Verification error:', err);
        setError('Failed to verify subscription');
        setIsVerifying(false);
      }
    };

    verifyCheckout();
  }, [getToken, refreshSubscription]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment</h2>
            <p className="text-slate-400 text-center">
              Please wait while we confirm your subscription...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-slate-400 text-center mb-6">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
          <p className="text-slate-400 text-center mb-6">
            Your subscription has been activated. Redirecting you to the app...
          </p>
          <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
            <div className="bg-green-500 h-full animate-pulse" style={{ width: '100%' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
